import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ImageCarousel } from "./ImageCarousel";
import { styled } from "@mui/system";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import { ProductImageAvatar } from "./ProductImageAvatar";
import Typography from "@mui/material/Typography";
import { DataTable } from "./DataTable";

// Stack with hidden scrollbar
const StackNoScrollbar = styled(Stack)({
  overflow: "auto",
  scrollbarWidth: "none", // Hide the scrollbar for firefox
  "&::-webkit-scrollbar": {
    display: "none", // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
  },
});

// Colour List format
const colourListFormat = (itemColours) =>
  Array.isArray(itemColours)
    ? itemColours.map((colour) => colour.colour).join(", ")
    : "";

const itemColoursIncludeColour = (itemColours, colour) =>
  Array.isArray(itemColours) &&
  itemColours.some((itemColour) => itemColour.colour === colour);

export const ItemTable = ({
  data,
  loading,
  isWardrobe,
  itemFilter,
  setItemFilter,
}) => {
  const items = data?.item || [];

  const [openImageCarousel, setOpenImageCarousel] = useState(false);
  const [imageCarouselProduct, setImageCarouselProduct] = useState("");

  // Filter items by wardrobe
  const filteredItems = items.filter((value, index, self) => {
    return self.findIndex((v) => v.productCode === value.productCode) === index;
  });

  const rowCount = Number(data?.paging?.noOfRecords) || 0;

  const pagination = {
    page: (itemFilter?.page ?? 1) - 1,
    pageSize: itemFilter?.pageSize ?? 25,
  };

  const handlePaginationChange = (next) => {
    setItemFilter((prevState) => ({
      ...prevState,
      page: next.page + 1,
      pageSize: next.pageSize,
    }));
  };

  // Handle Product Image Click
  const handleImageClick = (value) => {
    setOpenImageCarousel(true);
    setImageCarouselProduct(value);
  };

  // Handle Image Carousel Close
  const handleClose = () => {
    setOpenImageCarousel(false);
  };

  const productColumn = {
    accessorKey: "productCode",
    header: "Product",
    meta: { minWidth: 500 },
    cell: (info) => (
      <Stack
        direction="row"
        spacing={2}
        sx={{
          justifyContent: "flex-start",
          alignItems: "center",
          py: 2,
          px: 1,
        }}
      >
        <IconButton
          onClick={() => handleImageClick(info.getValue())}
          sx={{ p: 0 }}
        >
          <ProductImageAvatar code={info.getValue()} />
        </IconButton>
        <ListItemText
          primary={info.row.original.name}
          secondary={
            <Typography sx={{ color: "text.secondary" }}>
              {info.getValue()}
            </Typography>
          }
        />
      </Stack>
    ),
  };

  // Show these columns for items filtered by wardrobeID
  const columnsWardrobe = [
    productColumn,
    {
      id: "itemType",
      accessorFn: (row) => row.itemWardrobes[0].itemType,
      header: "Category",
      meta: { minWidth: 100 },
    },
    {
      accessorKey: "gender",
      header: "Item Type",
      meta: { minWidth: 150 },
      cell: (info) => {
        const gender = info.getValue();
        const label =
          gender === "M" ? "Male" : gender === "F" ? "Female" : "Neutral";
        return <Chip variant="outlined" clickable={false} label={label} />;
      },
    },
    {
      id: "entitlementQty",
      accessorFn: (row) => row.itemWardrobes[0].entitlementQty,
      header: "Entitlement Qty",
      meta: { minWidth: 150 },
    },
    {
      id: "entitlementPeriod",
      accessorFn: (row) => row.itemWardrobes[0].entitlementPeriod,
      header: "Entitlement Period (days)",
      meta: { minWidth: 200 },
    },
    {
      id: "itemColours",
      accessorFn: (row) => colourListFormat(row.itemWardrobes[0].itemColours),
      header: "Available Colours",
      meta: { minWidth: 300 },
    },
  ];

  // Show these columns for all items page
  const columns = [
    productColumn,
    { accessorKey: "colour", header: "Colour", meta: { minWidth: 100 } },
    {
      accessorKey: "itemWardrobes",
      header: "Available In",
      meta: { minWidth: 400 },
      cell: (info) => {
        const colour = info.row.original.colour;

        const wardrobes = info
          .getValue()
          .filter((wardrobe) =>
            itemColoursIncludeColour(wardrobe.itemColours, colour),
          )
          .map((wardrobe) => (
            <Chip
              key={wardrobe.wardrobeID}
              variant="outlined"
              clickable={true}
              label={wardrobe.wardrobeName}
              component={Link}
              to={`/wardrobes/${wardrobe.wardrobeID}`}
            />
          ));

        return (
          <StackNoScrollbar
            direction="row"
            spacing={1}
            sx={{ overflow: "auto" }}
          >
            {wardrobes}
          </StackNoScrollbar>
        );
      },
    },
  ];

  return (
    <>
      <DataTable
        aria-label="Items"
        columns={isWardrobe ? columnsWardrobe : columns}
        rows={isWardrobe ? filteredItems : items}
        getRowId={(row) => row.no}
        rowCount={rowCount}
        page={pagination.page}
        pageSize={pagination.pageSize}
        loading={loading}
        onPaginationChange={handlePaginationChange}
      />
      <ImageCarousel
        open={openImageCarousel}
        productCode={imageCarouselProduct}
        handleClose={handleClose}
      />
    </>
  );
};
